<script setup lang="ts">
import OmniDialog from "@/components/omni/OmniDialog.vue";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Role } from "@/constants/navigation";
import { useAppStore } from "@/store/app.store";
import { useChat } from "@/composables/useChat";
import { useBaseView } from "@/composables/useBaseView";
import type { ChatStateThread } from "@/store/chat.store";
import type { DispatcherDto } from "@/api/dispatchers/dto/dispatcher.dto";

const props = withDefaults(
  defineProps<{
    thread: ChatStateThread;
    disabled?: boolean;
  }>(),
  {
    disabled: false,
  },
);

const { t } = useI18n();
const appStore = useAppStore();
const { updateThread } = useChat();
const { roles } = useBaseView();

const dialogVisible = ref(false);
const transferToId = ref<number | null>(null);

const operationsFirst = computed(() => {
  const operations: DispatcherDto[] = [];
  const others: DispatcherDto[] = [];

  for (const dispatcher of appStore.dispatchers) {
    if (dispatcher.id === appStore.dispatcher?.id) continue;

    if (dispatcher.roles.includes(Role.operation)) {
      operations.push(dispatcher);
    } else {
      others.push(dispatcher);
    }
  }

  return operations.concat(others);
});

function transfer() {
  if (transferToId.value !== null) {
    updateThread(props.thread, { dispatcherId: transferToId.value });
  }
  dialogVisible.value = false;
}

function rolesToString(dispatcher: DispatcherDto) {
  return dispatcher.roles.map((roleId) => roles.find((r) => r.id === roleId)?.name).join(", ");
}
</script>

<template>
  <v-btn
    v-if="thread.dispatcherId === appStore.dispatcher?.id && !thread.resolved"
    :disabled="disabled"
    class="mr-2"
    color="primary"
    size="small"
    variant="outlined"
    @click="dialogVisible = true"
  >
    <v-icon class="text-primary mr-1"> mdi-swap-horizontal</v-icon>
    <span class="font-weight-600 text-uppercase text-primary">
      {{ t("dispatchers.transfer") }}
    </span>
  </v-btn>

  <omni-dialog
    v-model="dialogVisible"
    primary-action-label="Transfer"
    title="Transfer chat to"
    @primary-action:click="transfer"
  >
    <div class="my-5">
      <v-autocomplete
        v-model="transferToId"
        :items="operationsFirst"
        autocomplete="off"
        color="primary"
        density="compact"
        hide-details
        item-title="name"
        item-value="id"
        label="Select dispatcher"
        variant="outlined"
      >
        <template #item="{ item, props: itemProps }">
          <v-list-item :title="item.raw.name ?? ''" v-bind="itemProps">
            <span class="text-caption text-grey-darken-1">{{ rolesToString(item.raw) }}</span>
          </v-list-item>
        </template>
      </v-autocomplete>
    </div>
  </omni-dialog>
</template>

<style lang="scss" scoped></style>
